import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, PillTag } from './ui';
import {
  useC360List, C360Table, C360CardGrid, ViewSwitch, DEFAULT_VIEW_MODES, ColumnChooser, MultiGroupPicker,
  KpiCard, DonutCard, ViewIcon, EditIcon, DeleteIcon, CardIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

// #c360 Personel Tanımlama listesi ortak c360 altyapısına taşındı (Ürünler deseni).
// Veri: /api/personnel/odata (OData; $filter/$orderby/$count/$apply). Silme düz REST DELETE.
const API_ODATA = '/api/personnel/odata';
const API_REST = '/api/personnel';
const HIERARCHY_API = '/api/sales-org-hierarchy';

const WORK_STATUS_META = {
  ACTIVE: { label: 'Aktif Çalışıyor', tone: 'ok' },
  PASSIVE: { label: 'Pasif / Ayrıldı', tone: 'neutral' },
  LEAVE: { label: 'İzinde', tone: 'wait' },
};
const WORK_STATUS_OPTIONS = Object.entries(WORK_STATUS_META).map(([value, meta]) => ({ value, label: meta.label }));
const workStatusLabel = (v) => WORK_STATUS_META[v]?.label || '-';

const IS_ACTIVE_OPTIONS = [
  { value: true, label: 'Aktif' },
  { value: false, label: 'Pasif' },
];

const COLUMN_FILTER_META = {
  sicilId: { type: 'text', odataField: 'SicilId' },
  firstName: { type: 'text', odataField: 'FirstName' },
  lastName: { type: 'text', odataField: 'LastName' },
  levelNo: { type: 'number', odataField: 'LevelNo' },
  position: { type: 'text', odataField: 'Position' },
  phone: { type: 'text', odataField: 'Phone' },
  email: { type: 'text', odataField: 'Email' },
  workStatus: { type: 'select', odataField: 'WorkStatus', valueFormat: 'string', options: WORK_STATUS_OPTIONS },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: IS_ACTIVE_OPTIONS },
};

const SEARCH_FIELDS = ['SicilId', 'FirstName', 'LastName', 'Position', 'Email'];

// Liste + Kart + (Kanban/Grafik korunur). Kart, Liste'nin hemen YANINDA (index 1).
const PERSONNEL_VIEW_MODES = (() => {
  const list = DEFAULT_VIEW_MODES.find((m) => m.value === 'list');
  const rest = DEFAULT_VIEW_MODES.filter((m) => m.value !== 'list');
  const cardMode = { value: 'card', label: 'Kart', icon: CardIcon };
  return list ? [list, cardMode, ...rest] : [cardMode, ...DEFAULT_VIEW_MODES];
})();

const GROUP_OPTIONS = [
  { value: 'levelNo', label: 'Seviye' },
  { value: 'workStatus', label: 'Çalışma Durumu' },
];
const GROUP_BY_ODATA = {
  levelNo: { field: 'LevelNo', type: 'number' },
  workStatus: { field: 'WorkStatus', type: 'string' },
};

// === Grafik görünümü ===
function ChartView({ rows, levelLabel }) {
  const total = rows.length;
  const active = rows.filter((r) => r.isActive).length;
  const wsCount = (k) => rows.filter((r) => (r.workStatus || '') === k).length;

  const levelCounts = {};
  rows.forEach((r) => {
    const lbl = levelLabel(r.levelNo);
    levelCounts[lbl] = (levelCounts[lbl] || 0) + 1;
  });
  const levelData = Object.entries(levelCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 12);

  const wsPalette = { ok: '#00c875', neutral: '#94a3b8', wait: '#fdab3d' };
  const wsCounts = {};
  rows.forEach((r) => {
    const k = r.workStatus || '__none__';
    wsCounts[k] = (wsCounts[k] || 0) + 1;
  });
  const wsData = Object.entries(wsCounts).map(([k, v]) => {
    const meta = WORK_STATUS_META[k];
    return { name: meta?.label || 'Belirtilmemiş', value: v, color: meta ? wsPalette[meta.tone] : '#cbd5e1' };
  });

  return (
    <div className="c360-dashboard">
      <div className="c360-kpi-grid">
        <KpiCard label="Toplam Personel" value={total.toLocaleString('tr-TR')} />
        <KpiCard label="Aktif" value={active.toLocaleString('tr-TR')} hint={total > 0 ? `%${((active / total) * 100).toFixed(0)} aktif` : '—'} />
        <KpiCard label="Aktif Çalışıyor" value={wsCount('ACTIVE').toLocaleString('tr-TR')} />
        <KpiCard label="Pasif / Ayrıldı" value={wsCount('PASSIVE').toLocaleString('tr-TR')} />
        <KpiCard label="İzinde" value={wsCount('LEAVE').toLocaleString('tr-TR')} />
      </div>

      <div className="c360-chart-panel">
        <div className="c360-chart-panel-head">
          <span className="c360-chart-panel-title">Seviyeye Göre Dağılım</span>
          <span className="c360-chart-panel-sub">İlk {levelData.length} seviye</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={levelData} margin={{ top: 12, right: 8, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-25} textAnchor="end" interval={0} height={60} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'rgba(14, 165, 233, 0.06)' }} formatter={(v) => [`${v} personel`, 'Adet']} />
            <Bar dataKey="value" fill="#0ea5e9" radius={[8, 8, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontWeight: 800, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="c360-chart-donuts">
        <DonutCard title="Çalışma Durumu" subtitle={`${total} personel`} data={wsData} />
      </div>
    </div>
  );
}

export function PersonnelPage() {
  usePageMeta('Personel Tanımlama', 'Satış organizasyonu personellerini ve izin bilgilerini yönetin.');
  const navigate = useNavigate();
  // #900 Faz 3: Menü aksiyon hakları — Yeni/Değiştir/Sil butonlarını gate'ler
  // (GetMyMenuRights; kısıt yoksa default-open). Sunucu tarafı [RequireMenuAction] ile eşleşir.
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [levelNames, setLevelNames] = useState([]);
  const [hierarchyLoaded, setHierarchyLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({ email: false });

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const levelLabel = useCallback((no) => levelNames[no - 1] || `Seviye ${no}`, [levelNames]);

  useEffect(() => {
    let cancelled = false;
    fetch(HIERARCHY_API, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (cancelled) return;
        const data = payload?.data ?? payload?.Data ?? payload;
        setLevelNames(data?.levelNames ?? data?.LevelNames ?? []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setHierarchyLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const getKeyLabel = useCallback((field, row) => {
    let rawKey = row[field];
    if (rawKey === undefined || rawKey === null || rawKey === '') rawKey = '__none__';
    if (field === 'levelNo') {
      const key = rawKey === '__none__' ? '__none__' : Number(rawKey);
      return { key, label: key === '__none__' ? 'Atanmamış' : levelLabel(key) };
    }
    // workStatus
    const key = rawKey;
    return { key, label: key === '__none__' ? 'Belirtilmemiş' : (WORK_STATUS_META[key]?.label || String(key)) };
  }, [levelLabel]);

  const seedKanbanBuckets = useCallback((groupField) => {
    if (groupField === 'levelNo') return levelNames.map((name, i) => ({ key: i + 1, label: name || `Seviye ${i + 1}` }));
    if (groupField === 'workStatus') return WORK_STATUS_OPTIONS.map((o) => ({ key: o.value, label: o.label }));
    return [];
  }, [levelNames]);

  const list = useC360List({
    apiBase: API_ODATA,
    columnFilterMeta: COLUMN_FILTER_META,
    groupByOdata: GROUP_BY_ODATA,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'FirstName',
    defaultKanbanGroup: 'levelNo',
    getKeyLabel,
    seedKanbanBuckets,
    ready: hierarchyLoaded,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading, loadingMore,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    viewMode, setViewMode, groupBy, setGroupBy, isGrouping,
    expandedGroups, toggleGroupExpand, bucketCounts, bucketLoadingByPath,
    groupBuckets, nestedGroupBuckets, fetchData, loadMore, exportRows,
  } = list;

  const openNew = () => navigate('/personnel/new');
  const openDetail = (row = selected) => { if (row) navigate(`/personnel/${row.id}`); };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_REST}/${deleteConfirm.id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || `Silme başarısız (${res.status})`);
      showToast('success', 'Personel silindi.');
      setDeleteConfirm(null);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('sicilId', {
      header: 'Sicil ID', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor((row) => `${row.firstName || ''} ${row.lastName || ''}`.trim(), {
      id: 'firstName', header: 'Ad Soyad', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <strong>{v}</strong> : '-'; },
    }),
    columnHelper.accessor('levelNo', {
      header: 'Seviye', meta: { filterType: 'number' },
      cell: (info) => { const v = info.getValue(); return v ? <PillTag tone="info">{levelLabel(v)}</PillTag> : '-'; },
    }),
    columnHelper.accessor('position', { header: 'Pozisyon', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('phone', { header: 'Telefon', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('email', { header: 'E-posta', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('workStatus', {
      header: 'Çalışma Durumu', meta: { filterType: 'select' },
      cell: (info) => {
        const v = info.getValue();
        const meta = WORK_STATUS_META[v];
        return meta ? <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge> : '-';
      },
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [levelLabel, canEdit, canDelete]);

  const resolveFilterProps = useCallback((columnId, meta) => ({ options: meta.options }), []);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('FirstName', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'sicilId', headerName: 'Sicil ID' },
        { headerName: 'Ad Soyad', valueGetter: ({ row }) => `${row.firstName || ''} ${row.lastName || ''}`.trim() },
        { headerName: 'Seviye', valueGetter: ({ row }) => levelLabel(row.levelNo) },
        { field: 'position', headerName: 'Pozisyon' },
        { field: 'phone', headerName: 'Telefon' },
        { field: 'email', headerName: 'E-posta' },
        { headerName: 'Çalışma Durumu', valueGetter: ({ row }) => workStatusLabel(row.workStatus) },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive ? 'Aktif' : 'Pasif') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `personeller_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const handleKanbanColScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMore();
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Sicil / ad / pozisyon ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      {viewMode !== 'chart' && <MultiGroupPicker value={groupBy} onChange={setGroupBy} options={GROUP_OPTIONS} />}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Personel Tanımlama"
        subtitle="Satış organizasyonu personellerini filtreleyin, sıralayın ve yönetin."
        actions={(
          <>
            <ViewSwitch value={viewMode} onChange={setViewMode} modes={PERSONNEL_VIEW_MODES} />
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Personel</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={(viewMode === 'kanban' || viewMode === 'chart' || isGrouping) ? null : footerNode}
      >
        {viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <ChartView rows={rows} levelLabel={levelLabel} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'kanban' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <div className="c360-kanban">
              {groupBuckets.map((bucket) => (
                <div key={String(bucket.key)} className="c360-kanban-col">
                  <div className="c360-kanban-col-head">
                    <span className="c360-kanban-col-title">{bucket.label}</span>
                    <span className="c360-kanban-col-count">{bucketCounts[String(bucket.key)] != null ? bucketCounts[String(bucket.key)] : bucket.rows.length}</span>
                  </div>
                  <div className="c360-kanban-col-body" onScroll={handleKanbanColScroll}>
                    {bucket.rows.length === 0 ? (
                      <div className="c360-kanban-empty">Kayıt yok</div>
                    ) : (
                      bucket.rows.map((row) => {
                        const wsMeta = WORK_STATUS_META[row.workStatus];
                        return (
                          <div key={row.id} className="c360-kanban-card" onClick={() => setSelected(row)} onDoubleClick={() => openDetail(row)}>
                            <div className="c360-kanban-card-head">
                              <span className="c360-kanban-card-title" title={`${row.firstName} ${row.lastName}`}>{`${row.firstName || ''} ${row.lastName || ''}`.trim() || '-'}</span>
                            </div>
                            <div className="c360-kanban-card-meta">
                              {row.sicilId && <span className="c360-erp-chip">{row.sicilId}</span>}
                              {wsMeta && <StatusBadge tone={wsMeta.tone}>{wsMeta.label}</StatusBadge>}
                              {row.isActive ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>}
                            </div>
                            {row.position && <div className="c360-kanban-card-sub">{row.position}</div>}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalCount > 0 && (
              <div className="c360-kanban-loadbar">
                {loadingMore
                  ? `⏳ Yükleniyor… (${rows.length} / ${totalCount})`
                  : rows.length >= totalCount
                    ? `✓ Tümü yüklendi · ${totalCount} kayıt`
                    : `${rows.length} / ${totalCount} kayıt yüklü · Devamı için bir kolonun içinde aşağı kaydırın`}
              </div>
            )}
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'card' ? (
          <C360CardGrid
            rows={table.getRowModel().rows.map((r) => r.original)}
            card={(row) => {
              const wsMeta = WORK_STATUS_META[row.workStatus];
              const fullName = `${row.firstName || ''} ${row.lastName || ''}`.trim();
              return {
                avatarName: fullName,
                avatarTone: 'rep',
                title: fullName || '-',
                subtitle: row.sicilId,
                statusTone: wsMeta ? wsMeta.tone : (row.isActive ? 'ok' : 'neutral'),
                statusLabel: wsMeta ? wsMeta.label : (row.isActive ? 'Aktif' : 'Pasif'),
                meta: [
                  { k: 'Seviye', v: levelLabel(row.levelNo) },
                  row.position ? { k: 'Pozisyon', v: row.position } : null,
                  row.phone ? { k: 'Telefon', v: row.phone } : null,
                ].filter(Boolean),
              };
            }}
            actions={(row) => [
              { kind: 'view', title: 'Görüntüle', onClick: openDetail },
              { kind: 'edit', title: 'Değiştir', onClick: openDetail, hidden: !canEdit },
              { kind: 'delete', title: 'Sil', onClick: (r) => { setSelected(r); setDeleteConfirm(r); }, hidden: !canDelete },
            ]}
            onCardClick={setSelected}
            onCardDoubleClick={openDetail}
            selectedId={selected?.id}
            loading={loading}
          />
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={COLUMN_FILTER_META}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            resolveFilterProps={resolveFilterProps}
            isGrouping={isGrouping}
            nestedGroupBuckets={nestedGroupBuckets}
            expandedGroups={expandedGroups}
            toggleGroupExpand={toggleGroupExpand}
            bucketCounts={bucketCounts}
            bucketLoadingByPath={bucketLoadingByPath}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openDetail}
            emptyText="Kayıt bulunamadı"
          />
        )}
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                <strong>"{deleteConfirm.firstName} {deleteConfirm.lastName}"</strong> personelini silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnDanger, opacity: deleting ? 0.6 : 1 }} onClick={handleDelete} disabled={deleting}>{deleting ? 'Siliniyor...' : 'Evet, Sil'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(null)} disabled={deleting}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default PersonnelPage;
